import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// Panel reutilizable de recomendaciones IA por módulo.
// Uso: <AIRecommendationsPanel module="dashboard" /> o module="vehicle" entityId={id}
import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper, Box, Typography, Stack, Chip, IconButton, Button, Collapse,
  CircularProgress, Tooltip, alpha,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { api } from '../../services/api';

const SEV = {
  critical: { color: '#d32f2f', label: i18n.t('hardcoded.pages_systemusagepage.critico') },
  warning: { color: '#ff9800', label: i18n.t('hardcoded.pages_chatanalyticspage.atencion') },
  info: { color: '#2196f3', label: 'Info' },
};

const COLLAPSE_KEY = (m) => `aps_ai_panel_collapsed_${m}`;
const dedupeRecs = (list) => {
  const seen = new Set();
  const out = [];
  for (const r of list || []) {
    const key = `${(r.title || '').trim().toLowerCase()}|${(r.recommendation || '').trim().toLowerCase().slice(0, 60)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
};

export default function AIRecommendationsPanel({ module, entityId = null, title: titleProp, dense = false }) {
  const { t } = useTranslation();
  const title = titleProp ?? t('hardcoded.components_ai_airecommendationspanel.recomendaciones_ia');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY(module)) !== '1'; } catch (e) { return true; }
  });
  const [showAll, setShowAll] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState(false);

  const toggleExpanded = () => setExpanded((v) => {
    const next = !v;
    try { localStorage.setItem(COLLAPSE_KEY(module), next ? '0' : '1'); } catch (e) { /* noop */ }
    return next;
  });
  const MAX_VISIBLE = 3;

  const load = useCallback(async (generate = false) => {
    setLoading(true);
    setError(false);
    try {
      if (generate) {
        const r = await api.ai.generateRecommendations({ module, entity_id: entityId, force: true });
        setItems(dedupeRecs(r.data || []));
      } else {
        const r = await api.ai.listRecommendations({ module, entity_id: entityId, limit: 8 });
        let data = r.data || [];
        if (data.length === 0) {
          // generar bajo demanda la primera vez
          const g = await api.ai.generateRecommendations({ module, entity_id: entityId, force: false });
          data = g.data || [];
        }
        setItems(dedupeRecs(data));
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) setEnabled(false);
      else setError(true);
    } finally {
      setLoading(false);
    }
  }, [module, entityId]);

  useEffect(() => { load(false); }, [load]);

  const dismiss = async (id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
    try { await api.ai.updateRecommendation(id, 'dismissed'); } catch (e) { /* noop */ }
  };

  if (!enabled) return null;

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderColor: alpha('#d32f2f', 0.25),
      background: `linear-gradient(180deg, ${alpha('#d32f2f', 0.03)} 0%, transparent 60%)` }}>
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon sx={{ color: 'primary.main' }} fontSize="small" />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>{title}</Typography>
        {loading && <CircularProgress size={16} />}
        <Tooltip title={t('hardcoded.components_ai_airecommendationspanel.regenerar')}><span>
          <IconButton size="small" onClick={() => load(true)} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
        </span></Tooltip>
        {items.length > 0 && <Chip size="small" label={items.length} sx={{ height: 18, mr: 0.5 }} />}
        <Tooltip title={expanded ? t('hardcoded.components_ai_airecommendationspanel.colapsar') : t('hardcoded.components_ai_airecommendationspanel.expandir')}>
          <IconButton size="small" onClick={toggleExpanded}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.25 }}>
          {!loading && items.length === 0 && !error && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {t('hardcoded.components_ai_airecommendationspanel.sin_recomendaciones_por_ahora')}{' '}
              <Button size="small" onClick={() => load(true)}>{t('hardcoded.components_ai_airecommendationspanel.analizar_con_ia')}</Button>
            </Typography>
          )}
          {error && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {t('hardcoded.components_ai_airecommendationspanel.no_se_pudieron_cargar_recomendaciones')}<Button size="small" onClick={() => load(true)}>{t('hardcoded.components_ai_airecommendationspanel.reintentar')}</Button>
            </Typography>
          )}
          <Stack spacing={0.75}>
            {(showAll ? items : items.slice(0, MAX_VISIBLE)).map((rec) => {
              const sev = SEV[rec.severity] || SEV.info;
              return (
                <Paper key={rec.id} elevation={0} sx={{ p: 1, borderLeft: `3px solid ${sev.color}`, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip size="small" label={sev.label} sx={{ height: 18, fontSize: 10, bgcolor: alpha(sev.color, 0.12), color: sev.color, fontWeight: 800, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" component="span" fontWeight={700}>{rec.title}</Typography>
                      <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 0.5 }}>· {rec.recommendation}</Typography>
                    </Box>
                    <Tooltip title={t('hardcoded.components_ai_airecommendationspanel.ocultar')}><IconButton size="small" onClick={() => dismiss(rec.id)} sx={{ flexShrink: 0 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
          {items.length > MAX_VISIBLE && (
            <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ mt: 0.5 }}>
              {showAll ? t('hardcoded.components_ai_airecommendationspanel.ver_menos') : t('hardcoded.components_ai_airecommendationspanel.ver_itemslengthmax__mas', { itemslengthMAX_: items.length - MAX_VISIBLE })}
            </Button>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
